import { Button, Divider, Flex, Title } from '@mantine/core';
import { Stack } from '@mantine/core';
import { Text } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import CharacterCount from '@tiptap/extension-character-count';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { rem } from 'shared/styles/rem';

import { useCSSValuesInjectionProps } from '@/components/CSSVarsValuesProvider';
import { Modal } from '@/components/Modal';
import { Link as LinkIcon } from '@/icons/Link';
import { TextBold } from '@/icons/TextBold';
import { TextItalic } from '@/icons/TextItalic';
import { TextUnderline } from '@/icons/TextUnderline';
import { Unlink } from '@/icons/Unlink';
import * as classes from '@/utils/tiptap/extensions/Tooltip/components/Tooltip.css';
import { FontFamily } from '@/utils/tiptap/extensions/fontFamily';
import { FontSize } from '@/utils/tiptap/extensions/fontSize';
import { Heading } from '@/utils/tiptap/extensions/heading';
import { Image } from '@/utils/tiptap/extensions/image';
import { FontFamilySelect } from '@/widgets/_components/RichTextToolbar/FontFamilySelect';
import { InsertImage } from '@/widgets/_components/RichTextToolbar/InsertImage';
import { TextColorPicker } from '@/widgets/_components/RichTextToolbar/TextColorPicker';
import { TextFontSizeSelect } from '@/widgets/_components/RichTextToolbar/TextFontSizeSelect';

type TooltipModalProps = {
  content: string;
  onClose: (content: string) => void;
};

const CHARACTER_LIMIT = 250;

export function TooltipEditModal(props: TooltipModalProps) {
  const { content, onClose } = props;
  const { t } = useTranslation('utils', { keyPrefix: 'tooltipEditModal' });
  const [editorContentRef, setEditorContentRef] = useState<HTMLDivElement | null>(null);

  const cssVarsProps = useCSSValuesInjectionProps();

  const editor = useEditor({
    extensions: [
      Color,
      FontFamily,
      FontSize,
      Image,
      StarterKit.configure({ heading: false }),
      Placeholder.configure({ placeholder: t('placeholder') }),
      TextStyle,
      Underline,
      Link,
      CharacterCount.configure({ limit: CHARACTER_LIMIT }),
      Heading
    ],
    content
  });

  return (
    <Modal
      opened={true}
      onClose={() => onClose(content)}
      withinPortal={true}
      dataTestPrefix="tooltip-edit-modal"
      size="auto"
      classNames={{
        content: classes.modal,
        body: classes.modalBody,
        header: classes.modalHeader
      }}
      closeOnClickOutside={false}
      padding={`${rem(16)} ${rem(24)}`}
      closeButtonProps={{ className: classes.closeButton, iconSize: 24 }}
    >
      <div className={clsx(cssVarsProps.className, classes.modalBodyInner)} style={cssVarsProps.style}>
        <Title order={3} ta="center">
          {t('title')}
        </Title>
        <Stack gap={0}>
          <Stack gap={0}>
            <RichTextEditor editor={editor}>
              <InlineToolbar editorContentRef={editorContentRef} editor={editor} />
              <RichTextEditor.Content ref={setEditorContentRef} />
            </RichTextEditor>
          </Stack>
          <Text color="dark.3" className={classes.editorCounter}>
            {editor?.storage.characterCount.characters()}/{CHARACTER_LIMIT}
          </Text>
        </Stack>
        <Flex className={classes.buttonGroup}>
          <Button variant="subtle" color="gray" size="compact-lg" onClick={() => onClose(content)}>
            {t('cancel')}
          </Button>
          <Button
            size="compact-lg"
            disabled={editor?.storage.characterCount.characters() === 0}
            onClick={() => onClose(editor?.getHTML() ?? '')}
          >
            {t('save')}
          </Button>
        </Flex>
      </div>
    </Modal>
  );
}

type InlineToolbarProps = {
  editorContentRef: HTMLDivElement | null;
  editor: Editor | null;
};

function InlineToolbar(props: InlineToolbarProps) {
  const { editorContentRef, editor } = props;
  const { t } = useTranslation('widgets');
  return (
    <RichTextEditor.Toolbar className={classes.toolbarRoot} sticky stickyOffset={60}>
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <RichTextEditor.Control>
          <TextColorPicker portalTarget={editorContentRef} editor={editor} />
        </RichTextEditor.Control>
        <RichTextEditor.Underline title={t('underline') ?? ''} icon={TextUnderline} />
        <RichTextEditor.Italic title={t('italic') ?? ''} icon={TextItalic} />
        <RichTextEditor.Bold title={t('bold') ?? ''} icon={TextBold} />
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <InsertImage editor={editor} />
        <RichTextEditor.Link title={t('link') ?? ''} icon={LinkIcon} />
        {editor?.isActive('link') && <RichTextEditor.Unlink icon={Unlink} title={t('unlink') ?? ''} />}
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <TextFontSizeSelect editor={editor} />
      </RichTextEditor.ControlsGroup>
      <Divider className={classes.divider} />
      <RichTextEditor.ControlsGroup className={classes.controlsGroup}>
        <FontFamilySelect editor={editor} />
      </RichTextEditor.ControlsGroup>
    </RichTextEditor.Toolbar>
  );
}
